import React, { useEffect, useState } from 'react';
import apis from '../../HttpConfig/Api';
import logger from '../../logger/BatchedLogger';

const ApiLoggingTest = () => {
    const [testResults, setTestResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const addResult = (test, status, message) => {
        setTestResults(prev => [...prev, { test, status, message, timestamp: new Date().toISOString() }]);
    };

    const runTests = async () => {
        setIsLoading(true);
        setTestResults([]);

        // Test 1: Check logging configuration
        addResult('Logging Config', 'info', `Configuration: ${JSON.stringify(apis.loggingConfig)}`);

        // Test 2: Test manual logging
        try {
            logger.info('Manual test log', { testType: 'manual', component: 'ApiLoggingTest' });
            addResult('Manual Logging', 'success', 'Manual log sent successfully');
        } catch (error) {
            addResult('Manual Logging', 'error', `Failed: ${error.message}`);
        }

        // Test 3: Test API call logging (this should trigger interceptors)
        try {
            // Make a test API call (this will likely fail, but should log the attempt)
            await apis.instance.get('/test-endpoint');
            addResult('API Call Logging', 'success', 'API call logged successfully');
        } catch (error) {
            // Expected to fail, but should still log the attempt
            addResult('API Call Logging', 'info', `API call was logged despite error: ${error.message}`);
        }

        // Test 4: Test different log levels
        try {
            logger.debug('Debug test message');
            logger.info('Info test message');
            logger.warn('Warning test message');
            logger.error('Error test message');
            addResult('Log Levels', 'success', 'All log levels tested');
        } catch (error) {
            addResult('Log Levels', 'error', `Failed: ${error.message}`);
        }

        // Test 5: Test user action logging
        try {
            logger.logUserAction('test_button_click', { buttonId: 'test-button', page: 'ApiLoggingTest' });
            addResult('User Action Logging', 'success', 'User action logged successfully');
        } catch (error) {
            addResult('User Action Logging', 'error', `Failed: ${error.message}`);
        }

        // Test 6: Test performance logging
        try {
            logger.logPerformance('test_operation', 123, { operation: 'test', component: 'ApiLoggingTest' });
            addResult('Performance Logging', 'success', 'Performance metric logged successfully');
        } catch (error) {
            addResult('Performance Logging', 'error', `Failed: ${error.message}`);
        }

        // Test 7: Test error logging
        try {
            const testError = new Error('This is a test error');
            logger.logError(testError, { component: 'ApiLoggingTest', testType: 'error-logging' });
            addResult('Error Logging', 'success', 'Error logged successfully');
        } catch (error) {
            addResult('Error Logging', 'error', `Failed: ${error.message}`);
        }

        // Test 8: Force flush logs
        try {
            const bufferSize = logger.getBufferSize();
            await logger.forceFlush();
            addResult('Force Flush', 'success', `Flushed ${bufferSize} logs from buffer`);
        } catch (error) {
            addResult('Force Flush', 'error', `Failed: ${error.message}`);
        }

        setIsLoading(false);
    };

    useEffect(() => {
        // Log component mount
        logger.logComponentMount('ApiLoggingTest');

        return () => {
            logger.logComponentUnmount('ApiLoggingTest');
        };
    }, []);

    const getStatusColor = (status) => {
        switch (status) {
            case 'success': return '#4caf50';
            case 'error': return '#f44336';
            case 'info': return '#2196f3';
            default: return '#757575';
        }
    };

    return (
        <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
            <h2>API Logging Test Suite</h2>

            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <h3>Current Configuration:</h3>
                <pre style={{ backgroundColor: '#fff', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
                    {JSON.stringify(apis.loggingConfig, null, 2)}
                </pre>
            </div>

            <button
                onClick={runTests}
                disabled={isLoading}
                style={{
                    padding: '10px 20px',
                    backgroundColor: '#2196f3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    marginBottom: '20px'
                }}
            >
                {isLoading ? 'Running Tests...' : 'Run Logging Tests'}
            </button>

            <div style={{ marginBottom: '20px' }}>
                <button onClick={() => logger.info('Manual info log', { manual: true, timestamp: Date.now() })}>
                    Test Info Log
                </button>
                <button onClick={() => logger.warn('Manual warning log', { manual: true, timestamp: Date.now() })} style={{ marginLeft: '10px' }}>
                    Test Warning Log
                </button>
                <button onClick={() => logger.error('Manual error log', { manual: true, timestamp: Date.now() })} style={{ marginLeft: '10px' }}>
                    Test Error Log
                </button>
                <button onClick={() => alert(`Buffer size: ${logger.getBufferSize()}`)} style={{ marginLeft: '10px' }}>
                    Check Buffer Size
                </button>
            </div>

            {testResults.length > 0 && (
                <div>
                    <h3>Test Results:</h3>
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', maxHeight: '400px', overflowY: 'auto' }}>
                        {testResults.map((result, index) => (
                            <div
                                key={index}
                                style={{
                                    padding: '10px',
                                    borderBottom: index < testResults.length - 1 ? '1px solid #eee' : 'none',
                                    borderLeft: `4px solid ${getStatusColor(result.status)}`
                                }}
                            >
                                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                    {result.test} - <span style={{ color: getStatusColor(result.status) }}>{result.status.toUpperCase()}</span>
                                </div>
                                <div style={{ fontSize: '14px', color: '#666' }}>{result.message}</div>
                                <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                                    {new Date(result.timestamp).toLocaleTimeString()}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
                <h4>Instructions:</h4>
                <ol>
                    <li>Click "Run Logging Tests" to test all logging functionality</li>
                    <li>Use individual buttons to test specific log types</li>
                    <li>Check the browser's Network tab for requests to <code>/log-batch-frontend</code></li>
                    <li>Check the browser console for logging status messages</li>
                    <li>Verify logs appear in CloudWatch (if properly configured)</li>
                </ol>

                <h4>What to Check:</h4>
                <ul>
                    <li><strong>Network Tab:</strong> Look for POST requests to logging endpoints</li>
                    <li><strong>Console:</strong> Should show logging configuration and status</li>
                    <li><strong>CloudWatch:</strong> Frontend logs should appear in the frontend-logs stream</li>
                    <li><strong>Buffer Management:</strong> Buffer size should reset after flushing</li>
                </ul>
            </div>
        </div>
    );
};

export default ApiLoggingTest;
